/**
 * SOLID Review
 * S — Strong. One use case: the socket changed state — kept, and a resync started when one is owed.
 * O — Strong. When a resync is owed is the Domain's latch; nothing here decides it.
 * L — Strong. Implements its use-case role exactly.
 * I — Strong. `execute`.
 * D — Strong. The decisions port, the table's step and the resync, injected — the resync by its role.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { IPayloadConnectivity } from '../../domain/ports/schemas/TableEvents';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { IServiceSerialStep } from '../shared/ClientSteps';

export interface IConfigDepConnectivity {
  readonly decisions: IPortClientDecisions;
  readonly step: IServiceSerialStep<IPropsClientTableInput>;
  /** Catching up after a gap — started here, never awaited. */
  readonly resync: IUseCase<ICommandNothing, TPortOutcome>;
}

const NOTHING: ICommandNothing = {};

/**
 * THE SOCKET'S STATE, DELIVERED (FE1's `connectivity` case). A connection that comes back after a gap owes a resync, started
 * here and NOT awaited: the delivery is answered at once, and live events keep arriving while the catch-up reads run.
 */
export class ConnectivityUseCase implements IUseCase<IPayloadConnectivity, TPortOutcome> {
  constructor(private readonly deps: IConfigDepConnectivity) {}

  async execute(ctx: IContextRequest, payload: IPayloadConnectivity): Promise<TPortOutcome> {
    const { decisions, step, resync } = this.deps;
    const decided = await step.run(ctx, (input) => decisions.connectivity(ctx, input, payload));
    if (decided.success && decided.value.resync) void resync.execute(ctx, NOTHING);
    return step.outcome(decided);
  }
}
